export const ONBOARD_MIC_BALANCE_STORAGE_KEY =
  "formula-ae-ios-onboard-mic-balance-db-v2";
export const DEFAULT_ONBOARD_MIC_ATTENUATION_DB = -9;
export const MIN_ONBOARD_MIC_ATTENUATION_DB = -18;
export const MAX_ONBOARD_MIC_ATTENUATION_DB = 0;
export const ONBOARD_BALANCE_RAMP_SECONDS = 0.2;

type WebkitWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

export function isIosAudioSessionDevice(
  userAgent: string,
  platform: string,
  maxTouchPoints: number,
): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent)
    || (platform === "MacIntel" && maxTouchPoints > 1);
}

export function normalizeOnboardMicAttenuationDb(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_ONBOARD_MIC_ATTENUATION_DB;
  return Math.min(
    MAX_ONBOARD_MIC_ATTENUATION_DB,
    Math.max(MIN_ONBOARD_MIC_ATTENUATION_DB, Math.round(parsed)),
  );
}

export function decibelsToLinearGain(decibels: number): number {
  return 10 ** (normalizeOnboardMicAttenuationDb(decibels) / 20);
}

export function loadOnboardMicAttenuationDb(storage: Storage | null): number {
  if (!storage) return DEFAULT_ONBOARD_MIC_ATTENUATION_DB;
  try {
    const stored = storage.getItem(ONBOARD_MIC_BALANCE_STORAGE_KEY);
    return stored === null
      ? DEFAULT_ONBOARD_MIC_ATTENUATION_DB
      : normalizeOnboardMicAttenuationDb(stored);
  } catch {
    return DEFAULT_ONBOARD_MIC_ATTENUATION_DB;
  }
}

export function saveOnboardMicAttenuationDb(
  storage: Storage | null,
  decibels: number,
): void {
  if (!storage) return;
  try {
    storage.setItem(
      ONBOARD_MIC_BALANCE_STORAGE_KEY,
      String(normalizeOnboardMicAttenuationDb(decibels)),
    );
  } catch {
    // Private browsing and managed browsers can deny persistent storage.
  }
}

export class IosOnboardAudioBalanceController {
  private audioContext: AudioContext | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private gain: GainNode | null = null;
  private video: HTMLVideoElement | null = null;
  private microphoneActive = false;
  private attenuationDb = DEFAULT_ONBOARD_MIC_ATTENUATION_DB;

  enableFromUserGesture(video: HTMLVideoElement): void {
    this.video = video;
    this.ensureAudioPath(video);
    void this.audioContext?.resume().catch(() => {
      // A later play gesture retries resume; the native media path remains the
      // fallback when Web Audio is unavailable.
    });
    this.applyGain(false);
  }

  setMicrophoneActive(active: boolean): void {
    this.microphoneActive = active;
    this.applyGain(true);
  }

  setAttenuationDb(decibels: number): void {
    this.attenuationDb = normalizeOnboardMicAttenuationDb(decibels);
    this.applyGain(true);
  }

  close(): void {
    this.source?.disconnect();
    this.gain?.disconnect();
    void this.audioContext?.close().catch(() => {});
    this.source = null;
    this.gain = null;
    this.audioContext = null;
    this.video = null;
  }

  private ensureAudioPath(video: HTMLVideoElement): void {
    if (this.gain || this.audioContext) return;
    const AudioContextConstructor = window.AudioContext
      ?? (window as WebkitWindow).webkitAudioContext;
    if (!AudioContextConstructor) return;
    let audioContext: AudioContext | null = null;
    let source: MediaElementAudioSourceNode | null = null;
    try {
      audioContext = new AudioContextConstructor();
      source = audioContext.createMediaElementSource(video);
      const gain = audioContext.createGain();
      source.connect(gain);
      gain.connect(audioContext.destination);
      this.audioContext = audioContext;
      this.source = source;
      this.gain = gain;
    } catch {
      if (audioContext && source) {
        try {
          source.connect(audioContext.destination);
          this.audioContext = audioContext;
          this.source = source;
        } catch {
          void audioContext.close().catch(() => {});
        }
      } else {
        void audioContext?.close().catch(() => {});
      }
      this.gain = null;
    }
  }

  private applyGain(smooth: boolean): void {
    const target = this.microphoneActive
      ? decibelsToLinearGain(this.attenuationDb)
      : 1;
    if (this.gain && this.audioContext) {
      const parameter = this.gain.gain;
      const now = this.audioContext.currentTime;
      parameter.cancelScheduledValues(now);
      parameter.setValueAtTime(parameter.value, now);
      if (smooth) {
        parameter.linearRampToValueAtTime(target, now + ONBOARD_BALANCE_RAMP_SECONDS);
      } else {
        parameter.setValueAtTime(target, now);
      }
      return;
    }
    if (this.video) this.video.volume = target;
  }
}
